import * as React from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { toast } from 'react-toastify';
import { captureException } from '~/sentry';

type ErrorBoundaryProps = WrappedComponentProps & {
  children?: React.ReactNode;
};

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Error boundary caught', error, info);

    // React does not rethrow a caught render error, so the global handler
    // never sees it; report it here (still filtered by beforeSend)
    captureException(error, {
      contexts: { react: { componentStack: info.componentStack } },
      tags: { component: 'ErrorBoundary' },
    });

    const message = this.props.intl.formatMessage({
      id: 'unknown-error',
      defaultMessage: 'Something went wrong. Click here to reload.',
    });

    toast.error(message, {
      autoClose: false,
      onClick: () => window.location.reload(),
    });
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

const IntlErrorBoundary = injectIntl(ErrorBoundary);

export default IntlErrorBoundary;
